import type { TableMapDto as TableMapDtoV2, TableMapListResponse } from '@ordr-as/ordr-service-types/epos';
import {
  getGetTableMapsV2QueryKey,
  useBulkUpdateTableMapsV2,
  useGetTableMapByIdV2,
  useGetTableMapsSummaryV2,
  useGetTableMapsV2,
} from '@ordr-as/ordr-service-types/epos';
import { useQueryClient } from '@tanstack/react-query';

export const useQueryGetTableMap = (locationId: number, zoneId?: number, tableMapId?: string, enabled = true) => {
  const { queryKey: _queryKey, ...result } = useGetTableMapByIdV2(locationId, zoneId ?? 0, tableMapId ?? '', {
    query: {
      enabled: enabled && Boolean(locationId) && Boolean(zoneId) && Boolean(tableMapId),
      staleTime: 1000 * 60 * 5,
    },
  });
  return result;
};

export const useQueryGetTableMaps = (locationId: number, zoneId: number) => {
  const { queryKey: _queryKey, ...result } = useGetTableMapsV2(locationId, zoneId, undefined, {
    query: {
      enabled: Boolean(locationId) && Boolean(zoneId),
    },
  });
  return result;
};

export const useQueryGetTableMapSummary = (
  locationId: number,
  zoneId?: number,
  activeOnly?: boolean,
  excludeEmptyMaps?: boolean
) => {
  const { queryKey: _queryKey, ...result } = useGetTableMapsSummaryV2(
    locationId,
    zoneId ?? 0,
    { active: activeOnly, excludeEmptyMaps },
    {
      query: {
        enabled: Boolean(locationId) && Boolean(zoneId),
        staleTime: 1000 * 60 * 5,
      },
    }
  );
  return result;
};

export const useMutateBulkUpdateTableMaps = (locationId: number, zoneId: number) => {
  const queryClient = useQueryClient();
  // Optimistically write the published maps back into the getTableMaps cache and then invalidate
  // to fetch the server-canonical version. Consumers deriving state from the query (e.g.
  // hasUnpublishedChanges = tableMaps vs cache) flip immediately instead of waiting for the refetch.
  const { mutate, mutateAsync, ...rest } = useBulkUpdateTableMapsV2({
    mutation: {
      onSuccess: (_data, variables) => {
        const queryKey = getGetTableMapsV2QueryKey(variables.locationId, variables.zoneId);
        // BulkUpdateTableMapRequest.tableMaps is optional in the generated type, but our
        // wrapper's mutate signature requires it — safe to fall back to [] for the union narrow.
        const publishedTableMaps = variables.data.tableMaps ?? [];
        queryClient.setQueryData<TableMapListResponse>(queryKey, (old) =>
          old ? { ...old, tableMaps: publishedTableMaps } : { tableMaps: publishedTableMaps }
        );
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  return {
    ...rest,
    mutate: (tableMaps: TableMapDtoV2[], options?: Parameters<typeof mutate>[1]) =>
      mutate({ data: { tableMaps }, locationId, zoneId }, options),
    mutateAsync: (tableMaps: TableMapDtoV2[], options?: Parameters<typeof mutateAsync>[1]) =>
      mutateAsync({ data: { tableMaps }, locationId, zoneId }, options),
  };
};
